import { ADMIN_SERVICE_BASE_URL } from 'config';
import useAjax from 'hooks/use-ajax';
import invariant from 'invariant';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getPrimaryToken } from 'services/auth';

export interface IUseLegacyCollectionOptions {
  blockRequest?: boolean;
  collection: string;
  includeArchived?: number;
  limit?: number;
  pageOffset?: number;
  refreshKey?: string;
  search?: string;
  searchKey?: string,
  sortBy?: string;
  sortDirection?: string;
}

export interface IResponse<T> {
  data: {
    results: T[],
  } | null;
  error: object | null;
  loaded: boolean;
}

function throwError(error: string): never {
  return invariant(false, error) as never;
}

const useLegacyCollection = <T = any>(options: IUseLegacyCollectionOptions): Readonly<[T[], object | null, boolean]> => {
  const [pageResults, setResults] = useState<T[]>([]);
  const [ajaxError, setError] = useState<object | null>({});
  const [dataLoaded, setLoaded] = useState(false);

  const {
    collection = throwError('useLegacyCollection requires a collection'),
    limit = 1000,
    includeArchived = 0,
    pageOffset = 0,
    refreshKey,
    search = '',
    searchKey = '',
    sortBy = 'last_modified',
    sortDirection = 'desc',
    blockRequest,
  } = options;

  const token = useSelector(getPrimaryToken);
  const cacheInvalidation = useMemo(
    () => {
      return Date.now();
    },
    [refreshKey],
  );
  const url = `${ADMIN_SERVICE_BASE_URL}/legacy/${collection}?_=${cacheInvalidation}`;
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const params = {
    includeArchived,
    limit: limit.toString(),
    search,
    searchKey,
    skip: (limit * pageOffset).toString(),
    sortBy,
    sortDirection,
  };

  const pageState: IResponse<T> = useAjax({
    headers,
    params,
    url,
    blockRequest,
  });

  const { data, loaded, error } = pageState as IResponse<T>;

  useEffect(() => {
    if (loaded) {
      if (data) setResults(data.results);
    }
    setError(error);
    setLoaded(loaded);
  }, [data, loaded, error]);

  return [pageResults, ajaxError, dataLoaded] as const;
};

export default useLegacyCollection;
